import { configureStore, combineReducers, AnyAction } from "@reduxjs/toolkit";
import { persistencePrefsMiddleware } from "./persistence";

// App-level slices
import authReducer from "./slices/authSlice";
import themeReducer from "./slices/themeSlice";
import userSlice from "./slices/userSlice";
import workspaceReducer from "./slices/workspaceSlice";
import loadingReducer from "./slices/loadingSlice";
import viewReducer from "./slices/viewSlice";
import orgsReducer from "./slices/orgsSlice";

// Feature-specific slices
import canvasAuxReducer from "@features/annogram/canvas/state/canvasAuxSlice";
import stopwatchReducer from "@features/annogram/navbar/state/stopwatchSlice";
import cannedResponsesReducer from "@features/annogram/sidebar/state/cannedResponsesSlice";
import workspaceReadingsReducer from "@features/workspace-readings/state/workspaceReadingsSlice";
import workspaceCanvasesReducer from "@features/workspace-canvases/state/workspaceCanvasesSlice";
import workspacePapersReducer from "@features/workspace-papers/state/workspacePapersSlice";
import onboardingReducer from "@features/onboarding/state/onboardingSlice";

const rootReducer = combineReducers({
  workspace: workspaceReducer,
  canvasAux: canvasAuxReducer,
  auth: authReducer,
  theme: themeReducer,
  stopwatch: stopwatchReducer,
  user: userSlice,
  cannedResponses: cannedResponsesReducer,
  loading: loadingReducer,
  view: viewReducer,
  orgs: orgsReducer,
  workspaceReadings: workspaceReadingsReducer,
  workspaceCanvases: workspaceCanvasesReducer,
  workspacePapers: workspacePapersReducer,
  onboarding: onboardingReducer,
});

const resettableRootReducer = (
  state: ReturnType<typeof rootReducer> | undefined,
  action: AnyAction
) => {
  if (action.type === "auth/clearAll") {
    const preservedTheme = state?.theme;
    const fresh = rootReducer(undefined, action);
    return preservedTheme ? { ...fresh, theme: preservedTheme } : fresh;
  }
  return rootReducer(state, action);
};

export const store = configureStore({
  reducer: resettableRootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(persistencePrefsMiddleware),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type AppStore = typeof store;
